import { FC, useState, useMemo, useEffect } from 'react';
import { StepViewModel, IntegrationType } from '../../models/waterfall';
import { StepDetailsDialog } from './StepDetailsDialog';
import { StepNode } from './StepNode';
import { areAllStepsValid } from './stepUtils';
import IntegrationSettingsService from '../../services/integrationSettingsService';
import { IntegrationSettings } from '../../models/integrationSettings';

interface StepEditorProps {
  initialStep?: StepViewModel | null;
  onUpdateStep?: (step: StepViewModel) => void;
  onAddStep?: (parentStep: StepViewModel) => void;
  onDeleteStep?: (step: StepViewModel) => void;
  integrationType: IntegrationType;
}

export const StepEditor: FC<StepEditorProps> = ({ initialStep, onUpdateStep, onAddStep, onDeleteStep, integrationType }) => {
  const [selectedStep, setSelectedStep] = useState<StepViewModel | null>(null);
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [integrationSettings, setIntegrationSettings] = useState<IntegrationSettings[]>([]);

  const allStepsValid = useMemo(() => {
    return initialStep ? areAllStepsValid(initialStep) : true;
  }, [initialStep]);

  // Fetch integration settings for vendor names
  useEffect(() => {
    if (integrationType && integrationType !== IntegrationType.None) {
      const fetchSettings = async () => {
        try {
          const settings = await IntegrationSettingsService.getInstance().getByType(integrationType);
          setIntegrationSettings(settings);
        } catch (error) {
          console.error('Failed to fetch integration settings:', error);
          setIntegrationSettings([]);
        }
      };
      fetchSettings();
    }
  }, [integrationType]);

  const handleStepClick = (step: StepViewModel, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedStep(step);
    setIsPanelVisible(true);
  };

  const handleClosePanel = () => {
    setIsPanelVisible(false);
    setSelectedStep(null);
  };

  const handleSaveStep = (updatedStep: StepViewModel) => {
    onUpdateStep?.(updatedStep);
    handleClosePanel();
  };

  const handleAddStepClick = (parentStep: StepViewModel, e: React.MouseEvent) => {
    e?.stopPropagation();
    onAddStep?.(parentStep);
  };

  const handleDeleteStepClick = (step: StepViewModel, e: React.MouseEvent) => {
    e?.stopPropagation();
    onDeleteStep?.(step);
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-xl font-semibold m-0">Steps</h3>
      </div>

      <div className="border border-surface rounded-lg bg-slate-50 p-6">
        {initialStep ? (
          <StepNode
            step={initialStep}
            level={0}
            onStepClick={handleStepClick}
            onAddStepClick={handleAddStepClick}
            onDeleteStepClick={handleDeleteStepClick}
            allStepsValid={allStepsValid}
            integrationSettings={integrationSettings}
          />
        ) : (
          <div className="text-center text-color-secondary py-8">
            <i className="pi pi-inbox text-4xl mb-3"></i>
            <p className="text-lg">No steps configured yet</p>
            <p className="text-sm">Add an initial step to get started</p>
          </div>
        )}
      </div>

      <StepDetailsDialog step={selectedStep} visible={isPanelVisible} onClose={handleClosePanel} onSave={handleSaveStep} integrationType={integrationType} />
    </div>
  );
};
